// Utilidades para manejo de notificaciones
export interface NotificationPreferences {
  notificationsEnabled: boolean;
  lastUpdated: string;
  userId: string;
}

/**
 * Guarda las preferencias de notificaciones en localStorage
 */
export const saveNotificationPreferences = (isEnabled: boolean, userId: string): void => {
  try {
    const prefs: NotificationPreferences = {
      notificationsEnabled: isEnabled,
      lastUpdated: new Date().toISOString(),
      userId
    };
    
    localStorage.setItem('notification_preferences', JSON.stringify(prefs));
    console.log('✅ Preferencias de notificaciones guardadas:', prefs);
    
    // Verificar que se guardó correctamente
    const saved = localStorage.getItem('notification_preferences');
    if (saved) {
      const parsed = JSON.parse(saved);
      console.log('✅ Verificación: preferencias guardadas correctamente:', parsed);
    } else {
      console.warn('⚠️ Verificación: no se encontraron preferencias después de guardar');
    }
  } catch (error) {
    console.error('❌ Error guardando preferencias en localStorage:', error);
  }
};

/**
 * Carga las preferencias de notificaciones desde localStorage
 */
export const loadNotificationPreferences = (userId: string): NotificationPreferences | null => {
  try {
    console.log('🔍 Buscando preferencias en localStorage para usuario:', userId);
    const prefs = localStorage.getItem('notification_preferences');
    console.log('🔍 Raw localStorage data:', prefs);
    
    if (prefs) {
      const parsed = JSON.parse(prefs);
      console.log('🔍 Preferencias parseadas:', parsed);
      
      // Verificar que las preferencias sean del usuario actual
      if (parsed.userId === userId) {
        console.log('✅ Preferencias encontradas para usuario actual:', parsed);
        return parsed;
      } else {
        console.log('❌ Preferencias no coinciden con usuario actual:', {
          storedUserId: parsed.userId,
          currentUserId: userId
        });
        
        // Limpiar preferencias de usuario anterior
        localStorage.removeItem('notification_preferences');
        console.log('🧹 Preferencias de usuario anterior eliminadas');
      }
    } else {
      console.log('📱 No hay preferencias en localStorage');
    }
    return null;
  } catch (error) {
    console.error('❌ Error cargando preferencias del localStorage:', error);
    return null;
  }
};

/**
 * Limpia las preferencias de notificaciones
 */
export const clearNotificationPreferences = (): void => {
  try {
    localStorage.removeItem('notification_preferences');
    console.log('🧹 Preferencias de notificaciones eliminadas');
  } catch (error) {
    console.error('❌ Error eliminando preferencias:', error);
  }
};

/**
 * Valida y limpia preferencias corruptas del localStorage
 */
export const validateAndCleanPreferences = (userId: string): void => {
  try {
    console.log('🧹 Validando y limpiando preferencias del localStorage...');
    
    const prefs = localStorage.getItem('notification_preferences');
    if (!prefs) {
      console.log('✅ No hay preferencias para limpiar');
      return;
    }
    
    const parsed = JSON.parse(prefs);
    console.log('🔍 Preferencias encontradas:', parsed);
    
    // Verificar si las preferencias tienen la estructura correcta
    if (!parsed.hasOwnProperty('notificationsEnabled')) {
      console.warn('⚠️ Preferencias corruptas detectadas, limpiando...');
      localStorage.removeItem('notification_preferences');
      console.log('🧹 localStorage corrupto limpiado');
      return;
    }
    
    // Verificar si las preferencias son del usuario actual
    if (parsed.userId !== userId) {
      console.warn('⚠️ Preferencias de usuario anterior detectadas, limpiando...');
      localStorage.removeItem('notification_preferences');
      console.log('🧹 Preferencias de usuario anterior limpiadas');
      return;
    }
    
    console.log('✅ Preferencias validadas correctamente');
  } catch (error) {
    console.error('❌ Error validando preferencias:', error);
    // Si hay error al parsear, limpiar
    localStorage.removeItem('notification_preferences');
    console.log('🧹 localStorage corrupto limpiado por error de parsing');
  }
};

/**
 * Limpia el localStorage y establece un estado por defecto
 */
export const resetNotificationPreferences = (userId: string): void => {
  try {
    console.log('🔄 Reseteando preferencias de notificaciones...');
    
    // Limpiar localStorage
    localStorage.removeItem('notification_preferences');
    console.log('🧹 localStorage limpiado');
    
    // Establecer estado por defecto (inactivo)
    const defaultPrefs: NotificationPreferences = {
      notificationsEnabled: false,
      lastUpdated: new Date().toISOString(),
      userId
    };
    
    localStorage.setItem('notification_preferences', JSON.stringify(defaultPrefs));
    console.log('✅ Estado por defecto establecido:', defaultPrefs);
    
  } catch (error) {
    console.error('❌ Error reseteando preferencias:', error);
  }
};

/**
 * Verifica si el Service Worker está activo
 */
export const isServiceWorkerActive = async (): Promise<boolean> => {
  try {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      return !!(registration && registration.active);
    }
    return false;
  } catch (error) {
    console.error('❌ Error verificando Service Worker:', error);
    return false;
  }
};

/**
 * Espera a que el Service Worker esté activo
 */
export const waitForServiceWorker = async (maxAttempts = 10, baseDelay = 500): Promise<boolean> => {
  console.log('🔄 Esperando a que el Service Worker esté activo...');
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      // Verificar si el Service Worker está registrado y activo
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        
        if (registration && registration.active) {
          console.log('✅ Service Worker activo encontrado en intento', attempt);
          return true;
        }
        
        // Si hay un Service Worker instalando, esperar
        if (registration && registration.installing) {
          console.log('⏳ Service Worker instalando, esperando...');
          await new Promise(resolve => {
            const worker = registration.installing;
            if (worker) {
              worker.addEventListener('statechange', () => {
                if (worker.state === 'activated') {
                  resolve(true);
                }
              });
            }
          });
          return true;
        }
      }
      
      // Esperar antes del siguiente intento con backoff exponencial
      const delay = baseDelay * Math.pow(2, attempt - 1);
      console.log(`⏳ Intento ${attempt}/${maxAttempts}, esperando ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
      
    } catch (error) {
      console.warn(`⚠️ Error en intento ${attempt}:`, error);
      if (attempt === maxAttempts) {
        console.error('❌ Service Worker no pudo activarse después de', maxAttempts, 'intentos');
        return false;
      }
    }
  }
  
  return false;
};

/**
 * Verifica el estado de las notificaciones en el backend
 */
export const checkBackendNotificationStatus = async (userId: string, apiUrl: string, headers: HeadersInit): Promise<boolean> => {
  try {
    console.log('🔄 Verificando estado en backend para usuario:', userId);
    
    const response = await fetch(`${apiUrl}/fcm-tokens/${userId}`, {
      method: 'GET',
      headers
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log('📡 Respuesta del backend:', data);
      
      // Verificar si la respuesta tiene la estructura esperada
      if (data && typeof data === 'object') {
        // Nueva estructura del backend
        if (data.hasOwnProperty('hasToken')) {
          const hasValidToken = data.hasToken === true;
          console.log('✅ Estado del backend (hasToken):', hasValidToken);
          return hasValidToken;
        }
        // Estructura anterior (fallback)
        else if (data.hasOwnProperty('tokenFcm')) {
          const hasValidToken = data.tokenFcm && data.tokenFcm !== null;
          console.log('✅ Estado del backend (tokenFcm):', hasValidToken);
          return hasValidToken;
        } else {
          console.warn('⚠️ Respuesta del backend no tiene hasToken ni tokenFcm:', data);
          return false;
        }
      } else {
        console.warn('⚠️ Respuesta del backend no es un objeto válido:', data);
        return false;
      }
    } else {
      console.warn('⚠️ Respuesta no exitosa del backend:', response.status, response.statusText);
      return false;
    }
  } catch (error) {
    console.error('❌ Error verificando estado en backend:', error);
    return false;
  }
};

/**
 * Envía el token FCM al backend
 */
export const sendTokenToBackend = async (
  token: string | null, 
  userId: string, 
  apiUrl: string, 
  headers: HeadersInit
): Promise<void> => {
  try {
    console.log('📡 Enviando token al backend:', { 
      url: `${apiUrl}/fcm-tokens/${userId}`, 
      token: token ? `${token.substring(0, 20)}...` : 'null',
      userId 
    });
    
    const response = await fetch(`${apiUrl}/fcm-tokens/${userId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ 
        tokenFcm: token,
        hasToken: !!token  // Agregar hasToken para compatibilidad
      })
    });
    
    if (response.ok) {
      const result = await response.json();
      console.log('✅ Token enviado al backend exitosamente:', result);
    } else {
      const errorText = await response.text();
      console.error('❌ Error del backend:', { 
        status: response.status, 
        statusText: response.statusText,
        error: errorText 
      });
      throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
    }
  } catch (error) {
    console.error('❌ Error enviando token al backend:', error);
    throw error;
  }
};
